import { useCallback, useMemo } from 'react'
import { useIntl } from 'react-intl'
import { useSelector } from '@xstate/react'
import type { ActorRefFrom } from 'xstate'
import type multiCartMachine from 'machines/cart/multiCartMachine'
import { selectEffectiveVisibleCart, selectItemsInEffectiveVisibleCart, selectVisibleCartSummaries } from 'machines/cart/selectors'
import useExperiment from 'hooks/Experiments/useExperiment'
import { ChertoExperiments } from 'constants/experiments'
import { getCartViewItems, getUserCartViews } from 'lib/userCart/multiCartViews'
import { isDefaultMultiCart } from 'lib/userCart/multiCartVisibility'
import { trackPreviouslyViewedOffersIntentCounts } from 'analytics/snowplow/events/previouslyViewedOffersEvents'
import { EmptyObject } from 'lib/object/objectUtils'

export default function useUserCartViews(actor: ActorRefFrom<typeof multiCartMachine>, canActivate: boolean = false, showsCartNavigation: boolean = false) {
  const intl = useIntl()
  const carts = useSelector(actor, selectVisibleCartSummaries)
  const cart = useSelector(actor, selectEffectiveVisibleCart)
  const allItems = useSelector(actor, selectItemsInEffectiveVisibleCart)
  const activeCartView = useSelector(actor, snapshot => snapshot.context.activeCartView ?? 'cart')
  const itemsLoaded = useSelector(actor, snapshot => snapshot.context.cartItems.status === 'loaded')
  const defaultCart = carts.find(isDefaultMultiCart)
  const hasDefaultCart = !!defaultCart
  const defaultCartItems = useSelector(actor, snapshot => snapshot.context.cartItems.status === 'loaded' && defaultCart ? snapshot.context.cartItems.data[defaultCart.cartId] : undefined)
  const onActivation = useCallback(() => {
    trackPreviouslyViewedOffersIntentCounts(Object.values(defaultCartItems ?? EmptyObject))
  }, [defaultCartItems])
  const { isAnyVariant } = useExperiment(ChertoExperiments.croPreviouslyViewedOffers, {
    canActivate: canActivate && hasDefaultCart && itemsLoaded && !!defaultCartItems && (showsCartNavigation || isDefaultMultiCart(cart)),
    onActivation,
  })
  const splitDefault = isAnyVariant && hasDefaultCart
  const label = intl.formatMessage({
    id: '4sER5lfhC9UTBw4SItOO',
    defaultMessage: 'Previously Viewed Offers',
    description: 'Cart tab title for offers automatically saved when the customer reached checkout',
  })
  const views = useMemo(() => getUserCartViews(carts, splitDefault, label), [carts, splitDefault, label])
  const selectedView = views.find(candidate => candidate.cart.cartId === cart?.cartId && candidate.view === (splitDefault ? activeCartView : 'cart'))
    ?? views.find(candidate => candidate.cart.cartId === cart?.cartId && candidate.view === 'cart')
  const items = useMemo(() => getCartViewItems(allItems, cart, selectedView?.view ?? 'cart', splitDefault), [allItems, cart, selectedView?.view, splitDefault])
  return { views, selectedView, items, splitDefault }
}
